import clsx from 'clsx'

import Button from '../Button/Button'
import Icon from '../Icon/Icon'

export const ELLIPSIS = 'ellipsis'

export interface WindowedPaginationProps {
  /**
   * The currently active page (1-indexed).
   */
  currentPage: number
  /**
   * Total number of pages currently known/loaded.
   */
  totalPages: number
  /**
   * Called with the destination page when a number or the prev/next arrow is
   * clicked within the known range.
   */
  onPageChange: (page: number) => void
  /**
   * True when more pages exist beyond `totalPages` that have not been loaded
   * yet (cursor-based sources). Keeps Next enabled at the last known page and
   * shows a trailing hint.
   */
  hasMore?: boolean
  /**
   * Called when Next is pressed at the last known page while `hasMore` is true
   * — the parent should fetch the next batch.
   */
  onLoadMore?: () => void
  /**
   * Disables every control (e.g. while the next batch is loading).
   */
  isLoading?: boolean
}

/**
 * Builds the windowed sequence of page numbers to render, inserting
 * {@link ELLIPSIS} markers where pages are collapsed. Always keeps the first
 * and last page plus a one-page window either side of the current page, so the
 * control stays a fixed width whether there are 3 pages or 300.
 *
 * A gap of exactly one page is filled with that page rather than an ellipsis
 * (an ellipsis hiding a single number wastes the same space).
 *
 * @example paginationRange(56, 284) → [1, ELLIPSIS, 55, 56, 57, ELLIPSIS, 284]
 * @example paginationRange(2, 5)    → [1, 2, 3, 4, 5]
 */
export const paginationRange = (
  currentPage: number,
  totalPages: number,
  siblingCount = 1,
): (number | typeof ELLIPSIS)[] => {
  if (totalPages <= 0) return []

  const pages = new Set<number>([1, totalPages])
  for (
    let page = currentPage - siblingCount;
    page <= currentPage + siblingCount;
    page++
  ) {
    if (page >= 1 && page <= totalPages) pages.add(page)
  }

  const sorted = [...pages].sort((a, b) => a - b)
  const items: (number | typeof ELLIPSIS)[] = []
  let previous = 0

  for (const page of sorted) {
    const gap = page - previous
    if (gap === 2) {
      items.push(previous + 1)
    } else if (gap > 2) {
      items.push(ELLIPSIS)
    }
    items.push(page)
    previous = page
  }

  return items
}

/**
 * Windowed numeric pagination: prev/next arrows around a compact run of page
 * numbers with ellipses for the collapsed middle. Replaces the one-dot-per-page
 * pattern, which degenerates into an unusable line of dots for large sets.
 *
 * Purely controlled. Works for fully-known client-side sets and for
 * cursor-paginated sources that grow via `hasMore`/`onLoadMore`.
 */
export default function WindowedPagination({
  currentPage,
  totalPages,
  onPageChange,
  hasMore = false,
  onLoadMore,
  isLoading = false,
}: WindowedPaginationProps) {
  if (totalPages <= 1 && !hasMore) return null

  const isPrevDisabled = currentPage <= 1 || isLoading
  const isNextDisabled = (currentPage >= totalPages && !hasMore) || isLoading

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    } else if (hasMore) {
      onLoadMore?.()
    }
  }

  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-center lg:justify-start"
    >
      <Button
        className="w-11 h-11 [&]:min-h-0 rounded-full mr-6"
        disabled={isPrevDisabled}
        variant="secondary"
        onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
      >
        <Icon className="w-[26px] h-[26px]" icon="arrow-left" />
        <span className="sr-only">Previous page</span>
      </Button>
      <ol className="flex items-center gap-1">
        {paginationRange(currentPage, totalPages).map((item, index) =>
          item === ELLIPSIS ? (
            <li
              aria-hidden
              className="w-9 h-9 flex items-center justify-center text-neutral-500 select-none"
              key={`ellipsis-${index}`}
            >
              …
            </li>
          ) : (
            <li key={item}>
              <button
                aria-current={item === currentPage ? 'page' : undefined}
                aria-label={`Page ${item}`}
                className={clsx(
                  'w-9 h-9 rounded-full type-body-8 transition-colors',
                  item === currentPage
                    ? 'bg-theme-primary text-white font-semibold'
                    : 'text-neutral-700 hover:bg-neutral-200',
                )}
                disabled={isLoading}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            </li>
          ),
        )}
        {hasMore && (
          <li
            aria-hidden
            className="w-9 h-9 flex items-center justify-center text-neutral-500 select-none"
          >
            …
          </li>
        )}
      </ol>
      <Button
        className="w-11 h-11 [&]:min-h-0 rounded-full ml-6"
        disabled={isNextDisabled}
        variant="secondary"
        onClick={handleNext}
      >
        <Icon className="w-[26px] h-[26px]" icon="arrow-right" />
        <span className="sr-only">Next page</span>
      </Button>
    </nav>
  )
}
